import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { avisarSesionPerdida, guardarSesion, pedir, renovarSesion } from '../api/cliente';
import { tienePermiso } from '../comun/permisos';
import type { RespuestaIngreso, RespuestaRefresco, Yo } from '../api/tipos';

type Estado = { tipo: 'cargando' } | { tipo: 'afuera' } | { tipo: 'adentro'; yo: Yo };

interface Sesion {
  estado: Estado;
  /** Con `entidadId` cuando la persona trabaja en más de una y ya eligió. */
  ingresar: (identificador: string, clave: string, entidadId?: string) => Promise<void>;
  salir: () => Promise<void>;
  cambiarEntidad: (entidadId: string) => Promise<void>;
  volverAPlataforma: () => Promise<void>;
  recargar: () => Promise<void>;
}

const Contexto = createContext<Sesion | null>(null);

/**
 * El color de la entidad se inyecta EN RUNTIME sobre el token base: un solo build sirve a todas
 * las entidades, en claro y en oscuro. Todo lo demás (hover, fondos suaves, tintas) se deriva con
 * color-mix desde `--marca-base`, así alcanza con escribir un valor.
 */
function aplicarMarca(color: string | null | undefined): void {
  if (color) document.documentElement.style.setProperty('--marca-base', color);
  else document.documentElement.style.removeProperty('--marca-base');
}

export function ProveedorSesion({ children }: { children: ReactNode }) {
  const [estado, setEstado] = useState<Estado>({ tipo: 'cargando' });
  const consultas = useQueryClient();

  const cargarYo = useCallback(async () => {
    const yo = await pedir<Yo>('/auth/yo');
    aplicarMarca(yo.entidad?.colorMarca);
    document.title = yo.entidad ? `${yo.entidad.nombre} · Planilla` : 'Planilla';
    setEstado({ tipo: 'adentro', yo });
  }, []);

  useEffect(() => {
    avisarSesionPerdida(() => {
      guardarSesion(null);
      consultas.clear();
      setEstado({ tipo: 'afuera' });
    });
    // Al recargar la página el access token en memoria se perdió: se renueva con el refresh.
    renovarSesion()
      .then((token) => (token ? cargarYo() : setEstado({ tipo: 'afuera' })))
      .catch(() => setEstado({ tipo: 'afuera' }));
  }, [cargarYo, consultas]);

  const conTokens = useCallback(
    async (promesa: Promise<RespuestaRefresco>) => {
      const tokens = await promesa;
      guardarSesion(tokens.access, tokens.refresh);
      consultas.clear();
      await cargarYo();
    },
    [cargarYo, consultas],
  );

  const valor = useMemo<Sesion>(
    () => ({
      estado,
      ingresar: async (identificador, clave, entidadId) => {
        const respuesta = await pedir<RespuestaIngreso>('/auth/ingresar', { metodo: 'POST', cuerpo: { identificador, clave, entidadId } });
        guardarSesion(respuesta.access, respuesta.refresh);
        consultas.clear();
        await cargarYo();
      },
      salir: async () => {
        await pedir('/auth/salir', { metodo: 'POST' }).catch(() => undefined);
        guardarSesion(null);
        consultas.clear();
        aplicarMarca(null);
        setEstado({ tipo: 'afuera' });
      },
      cambiarEntidad: (entidadId) => conTokens(pedir<RespuestaRefresco>('/auth/entidad', { metodo: 'POST', cuerpo: { entidadId } })),
      volverAPlataforma: () => conTokens(pedir<RespuestaRefresco>('/auth/plataforma', { metodo: 'POST' })),
      recargar: cargarYo,
    }),
    [estado, cargarYo, conTokens, consultas],
  );

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useSesion(): Sesion {
  const sesion = useContext(Contexto);
  if (!sesion) throw new Error('useSesion fuera de ProveedorSesion');
  return sesion;
}

/** Para las pantallas de adentro: la sesión ya está garantizada por App. */
export function useYo(): Yo {
  const { estado } = useSesion();
  if (estado.tipo !== 'adentro') throw new Error('useYo sin sesión');
  return estado.yo;
}

/** La entidad activa. Las pantallas de entidad no se montan sin ella. */
export function useEntidad() {
  const yo = useYo();
  if (!yo.entidad) throw new Error('useEntidad sin entidad activa');
  return yo.entidad;
}

/** Ocultar un botón es cortesía; la API verifica el permiso por su cuenta de todos modos. */
export function usePuede(): (permiso: string) => boolean {
  const yo = useYo();
  return useCallback((permiso: string) => tienePermiso(yo.permisos, permiso), [yo.permisos]);
}

export function useMoneda(): string {
  return useYo().entidad?.moneda ?? 'PYG';
}

/** La zona de la entidad: con esto se muestran los instantes, nunca con la del navegador. */
export function useZona(): string {
  return useYo().entidad?.zonaHoraria ?? 'America/Asuncion';
}
